import { useEffect, useRef } from 'react';

/**
 * useScreenProtection
 *
 * Two-layer approach:
 *
 * Layer 1 — Keyboard blocking (works for: Ctrl+U, Ctrl+S, Ctrl+P, right-click)
 *           Does NOT work for F12 / Ctrl+Shift+I / Ctrl+Shift+C because
 *           Chrome handles those at the browser-process level, above JS reach.
 *
 * Layer 2 — DevTools DETECTION (window size polling + console trick)
 *           When DevTools is detected → page content is hidden instantly.
 *           This works regardless of how DevTools was opened.
 */
const useScreenProtection = () => {
  const blurRef = useRef(null);
  const devtoolsWarningRef = useRef(null);
  const detectorIntervalRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add('no-screenshot');

    // ══════════════════════════════════════════════════════════════════════
    // LAYER 1 — Blur overlay on real tab switch (debounced, not on screenshot)
    // ══════════════════════════════════════════════════════════════════════
    const blurOverlay = document.createElement('div');
    blurOverlay.id = 'wm-blur-overlay';
    Object.assign(blurOverlay.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483644',
      pointerEvents: 'none',
      display: 'none',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      background: 'rgba(0,0,0,0.55)',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '12px',
    });
    const lockIcon = document.createElement('div');
    lockIcon.textContent = '🔒';
    lockIcon.style.cssText = 'font-size:48px;';
    const lockText = document.createElement('div');
    lockText.textContent = 'Content hidden for privacy';
    lockText.style.cssText = 'color:#fff;font-size:18px;font-family:Inter,sans-serif;font-weight:600;';
    blurOverlay.appendChild(lockIcon);
    blurOverlay.appendChild(lockText);
    document.body.appendChild(blurOverlay);
    blurRef.current = blurOverlay;

    let blurTimer = null;
    const showTabBlur  = () => { blurOverlay.style.display = 'flex'; };
    const hideTabBlur  = () => { blurOverlay.style.display = 'none'; };
    const handleBlur   = () => { blurTimer = setTimeout(showTabBlur, 600); };
    const handleFocus  = () => { clearTimeout(blurTimer); hideTabBlur(); };
    const handleVis    = () => {
      if (document.hidden) { blurTimer = setTimeout(showTabBlur, 600); }
      else { clearTimeout(blurTimer); hideTabBlur(); }
    };
    document.addEventListener('visibilitychange', handleVis);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    // ══════════════════════════════════════════════════════════════════════
    // LAYER 2 — DevTools Detection + Content Hide
    // ══════════════════════════════════════════════════════════════════════
    const devtoolsWarning = document.createElement('div');
    devtoolsWarningRef.current = devtoolsWarning;
    Object.assign(devtoolsWarning.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483645',
      pointerEvents: 'all',
      display: 'none',
      background: '#0a0a0a',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '16px',
      fontFamily: 'Inter, sans-serif',
    });
    devtoolsWarning.innerHTML = `
      <div style="font-size:56px;">🚫</div>
      <div style="color:#cead80;font-size:22px;font-weight:700;">Developer Tools Detected</div>
      <div style="color:#888;font-size:15px;text-align:center;max-width:380px;line-height:1.6;">
        This content is protected.<br/>Please close Developer Tools to continue.
      </div>
    `;
    document.body.appendChild(devtoolsWarning);

    let devtoolsOpen = false;

    const showDevtoolsBlock = () => {
      devtoolsWarning.style.display = 'flex';
      // Also hide actual page content so it can't be inspected in Elements tab
      document.documentElement.style.visibility = 'hidden';
      devtoolsWarning.style.visibility = 'visible';
    };
    const hideDevtoolsBlock = () => {
      devtoolsWarning.style.display = 'none';
      document.documentElement.style.visibility = '';
    };

    // ── Mobile check ───────────────────────────────────────────────────────
    // Mobile browsers (iPhone, Android) have NO DevTools.
    // The window-size trick gives false positives on iPhone because Safari's
    // browser chrome (address bar + bottom bar) makes outerHeight - innerHeight > 160px.
    // So we skip DevTools detection entirely on mobile/touch devices.
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
      .test(navigator.userAgent) || window.matchMedia('(pointer: coarse)').matches;

    if (!isMobile) {
      // Method 1: Window size difference (docked DevTools shrinks innerWidth/Height)
      const THRESHOLD = 160;
      const checkWindowSize = () => {
        const widthDiff  = window.outerWidth  - window.innerWidth;
        const heightDiff = window.outerHeight - window.innerHeight;
        return widthDiff > THRESHOLD || heightDiff > THRESHOLD;
      };

      // Method 2: Console proxy trick (undocked/detached DevTools)
      let consoleDetected = false;
      const consoleDetector = new Proxy({}, {
        get(target, key) {
          if (key === 'toString' || key === Symbol.toPrimitive) {
            consoleDetected = true;
          }
          return Reflect.get(target, key);
        }
      });
      const silentCheck = () => {
        consoleDetected = false;
        console.log('%c', consoleDetector);
        return consoleDetected;
      };

      // Poll every 800ms — desktop only
      detectorIntervalRef.current = setInterval(() => {
        const detected = checkWindowSize() || silentCheck();
        if (detected && !devtoolsOpen) {
          devtoolsOpen = true;
          showDevtoolsBlock();
        } else if (!detected && devtoolsOpen) {
          devtoolsOpen = false;
          hideDevtoolsBlock();
        }
      }, 800);
    }

    // ══════════════════════════════════════════════════════════════════════
    // LAYER 3 — Keyboard shortcuts that CAN actually be blocked in browser
    // ══════════════════════════════════════════════════════════════════════
    const blockContextMenu = (e) => e.preventDefault();
    const blockDrag        = (e) => e.preventDefault();
    document.addEventListener('contextmenu', blockContextMenu);
    document.addEventListener('dragstart',   blockDrag);

    const blockKeys = (e) => {
      const key  = e.key?.toLowerCase();
      const ctrl  = e.ctrlKey || e.metaKey;
      const shift = e.shiftKey;
      const alt   = e.altKey;

      // These DO get blocked by preventDefault in browser:
      if (ctrl && key === 'u') { e.preventDefault(); e.stopPropagation(); return; } // View Source
      if (ctrl && key === 's') { e.preventDefault(); e.stopPropagation(); return; } // Save page
      if (ctrl && key === 'p') { e.preventDefault(); e.stopPropagation(); return; } // Print
      if (ctrl && shift && key === 'c') { e.preventDefault(); e.stopPropagation(); return; } // Inspect
      if (ctrl && alt  && key === 'c') { e.preventDefault(); e.stopPropagation(); return; } // Inspect (Mac)

      // F12, Ctrl+Shift+I/J — Chrome ignores preventDefault for these,
      // but the DevTools detector above handles the response.
    };
    document.addEventListener('keydown', blockKeys, true);

    // ══════════════════════════════════════════════════════════════════════
    // CLEANUP
    // ══════════════════════════════════════════════════════════════════════
    return () => {
      clearTimeout(blurTimer);
      clearInterval(detectorIntervalRef.current);
      document.documentElement.classList.remove('no-screenshot');
      document.documentElement.style.visibility = '';
      if (blurRef.current && document.body.contains(blurRef.current))
        document.body.removeChild(blurRef.current);
      if (devtoolsWarningRef.current && document.body.contains(devtoolsWarningRef.current))
        document.body.removeChild(devtoolsWarningRef.current);
      document.removeEventListener('visibilitychange', handleVis);
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('dragstart',   blockDrag);
      document.removeEventListener('keydown',     blockKeys, true);
      window.removeEventListener('blur',  handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);
};

export default useScreenProtection;
